import * as i2 from '@angular/common';
import { CommonModule, NgClass } from '@angular/common';
import * as i0 from '@angular/core';
import { forwardRef, Injector, Renderer2, ElementRef, Input, HostBinding, Inject, ViewEncapsulation, Component, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import * as i3 from '@angular/forms';
import { NG_VALUE_ACCESSOR, CheckboxControlValueAccessor, NgControl, NgModel, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@windmill/ng-windmill/shared';
import { CentricFirstLetterUppercasePipe } from '@windmill/ng-windmill/shared';

const WINDMILL_CHECKBOX_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => WindmillCheckboxComponent),
    multi: true
};
/**
 * To use this component in a module, you can import the `WindmillCheckboxModule`:
 * @example
 * ```typescript
 * import { WindmillCheckboxModule } from '@windmill/ng-windmill/checkbox';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillCheckboxComponent`:
 * @example
 * ```typescript
 * import { WindmillCheckboxComponent } from '@windmill/ng-windmill/checkbox';
 * ```
 */
class WindmillCheckboxComponent extends CheckboxControlValueAccessor {
    injector;
    uppercasePipe;
    /**
     * The label text for component
     */
    label;
    /**
     * Whether the checkbox is indeterminate
     */
    indeterminate = false;
    /**
     * Whether the checkbox is required
     */
    isRequired = false;
    /**
     * Name value will be applied to the input element if present
     */
    name;
    /**
     * The id for checkbox input
     */
    id;
    /**
     * The data-testid for component
     */
    testCheckboxId;
    /**
     * Sets the aria-label attribute for the input
     */
    ariaLabel;
    /**
     * Sets the aria-description attribute for the input
     */
    ariaDescription;
    /**
     * The aria-labelledby attribute which will be forwarded to the input element
     */
    ariaLabelledby;
    /**
     * The aria-describedby attribute which is read after the element's label and field type
     */
    ariaDescribedby;
    /**
     * Custom CSS background color (e.g. hex, rgba, CSS variable etc.) for the checkbox in an editable state
     */
    backgroundColor;
    /**
     * Whether the checkbox is readonly.
     */
    readonly = false;
    /**
     * The input value, if the component is readonly
     */
    readonlyInputValue;
    /**
     * The input size, if the component is readonly
     */
    readonlyInputSize;
    disabled = false;
    render2;
    elementRef;
    value = false;
    constructor(injector, render2, elementRef, uppercasePipe) {
        super(render2, elementRef);
        this.injector = injector;
        this.uppercasePipe = uppercasePipe;
        this.elementRef = elementRef;
        this.render2 = render2;
    }
    getFormControl() {
        return this.injector.get(NgControl).control;
    }
    writeValue(value) {
        this.value = value;
    }
    handleChange(event) {
        event.stopPropagation();
        this.writeValue(event.target.checked);
        if (this.indeterminate) {
            this.indeterminate = false;
        }
        if (this.injector.get(NgControl) instanceof NgModel) {
            this.onChange(this.value);
        }
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    get ariaCheckedValue() {
        if (this.indeterminate) {
            return 'mixed';
        }
        else if (this.getFormControl().value) {
            return 'true';
        }
        return 'false';
    }
    get tabIndexValue() {
        return this.disabled ? null : 0;
    }
    get hasDisabledAttribute() {
        if (this.disabled) {
            return true;
        }
        return null;
    }
    get hasRequiredAttribute() {
        if (!this.disabled &&
            (this.getFormControl().hasValidator(Validators.requiredTrue) ||
                (this.injector.get(NgControl) instanceof NgModel && this.isRequired))) {
            return true;
        }
        return null;
    }
    get readonlyValue() {
        return this.readonlyInputValue ? this.readonlyInputValue : this.uppercasePipe.transform(String(this.value));
    }
    get backgroundColorValue() {
        const shouldHaveBackgroundColor = !(this.readonly || this.disabled || this.getFormControl().value || this.indeterminate);
        return shouldHaveBackgroundColor ? this.backgroundColor : null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillCheckboxComponent, deps: [{ token: forwardRef(() => Injector) }, { token: forwardRef(() => Renderer2) }, { token: forwardRef(() => ElementRef) }, { token: i1.CentricFirstLetterUppercasePipe }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillCheckboxComponent, isStandalone: true, selector: "windmill-checkbox", inputs: { label: "label", indeterminate: "indeterminate", isRequired: "isRequired", name: "name", id: "id", testCheckboxId: "testCheckboxId", ariaLabel: "ariaLabel", ariaDescription: "ariaDescription", ariaLabelledby: "ariaLabelledby", ariaDescribedby: "ariaDescribedby", backgroundColor: "backgroundColor", readonly: "readonly", readonlyInputValue: "readonlyInputValue", readonlyInputSize: "readonlyInputSize" }, host: { properties: { "class.is-readonly": "this.readonly" } }, providers: [WINDMILL_CHECKBOX_VALUE_ACCESSOR, CentricFirstLetterUppercasePipe], usesInheritance: true, ngImport: i0, template: "@if (readonly) {\r\n  <div\r\n    class=\"readonly-checkbox windmill-input is-readonly has-value\"\r\n    [ngClass]=\"readonlyInputSize\">\r\n    <input\r\n      #inputReadonly\r\n      class=\"input-element\"\r\n      type=\"text\"\r\n      [attr.readonly]=\"readonly\"\r\n      [attr.value]=\"readonlyValue\"\r\n      [attr.id]=\"id\"\r\n      [attr.data-testid]=\"testCheckboxId\"\r\n      [attr.name]=\"name\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\" />\r\n  </div>\r\n} @else {\r\n  <div\r\n    class=\"windmill-checkbox\"\r\n    [class.is-disabled]=\"disabled\"\r\n    [class.has-label]=\"label\">\r\n    <input\r\n      type=\"checkbox\"\r\n      [formControl]=\"getFormControl()\"\r\n      [attr.checked]=\"getFormControl().value\"\r\n      [attr.indeterminate]=\"indeterminate\"\r\n      [attr.tabindex]=\"tabIndexValue\"\r\n      [attr.disabled]=\"hasDisabledAttribute\"\r\n      [attr.required]=\"hasRequiredAttribute\"\r\n      [attr.name]=\"name\"\r\n      [attr.id]=\"id\"\r\n      [attr.data-testid]=\"testCheckboxId\"\r\n      [attr.aria-checked]=\"ariaCheckedValue\"\r\n      [attr.aria-required]=\"hasRequiredAttribute\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\"\r\n      (change)=\"handleChange($event)\" />\r\n    <span class=\"checkbox-inner-container\">\r\n      <span class=\"checkbox-frame\"></span>\r\n      <span\r\n        class=\"checkbox-background\"\r\n        [style.backgroundColor]=\"backgroundColorValue\"></span>\r\n    </span>\r\n    @if (label) {\r\n      <label\r\n        class=\"checkbox-label\"\r\n        [attr.for]=\"id\">\r\n        {{ label }}\r\n      </label>\r\n    }\r\n  </div>\r\n}\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i3.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillCheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-checkbox', encapsulation: ViewEncapsulation.None, imports: [CommonModule, FormsModule, NgClass, ReactiveFormsModule], providers: [WINDMILL_CHECKBOX_VALUE_ACCESSOR, CentricFirstLetterUppercasePipe], standalone: true, template: "@if (readonly) {\r\n  <div\r\n    class=\"readonly-checkbox windmill-input is-readonly has-value\"\r\n    [ngClass]=\"readonlyInputSize\">\r\n    <input\r\n      #inputReadonly\r\n      class=\"input-element\"\r\n      type=\"text\"\r\n      [attr.readonly]=\"readonly\"\r\n      [attr.value]=\"readonlyValue\"\r\n      [attr.id]=\"id\"\r\n      [attr.data-testid]=\"testCheckboxId\"\r\n      [attr.name]=\"name\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\" />\r\n  </div>\r\n} @else {\r\n  <div\r\n    class=\"windmill-checkbox\"\r\n    [class.is-disabled]=\"disabled\"\r\n    [class.has-label]=\"label\">\r\n    <input\r\n      type=\"checkbox\"\r\n      [formControl]=\"getFormControl()\"\r\n      [attr.checked]=\"getFormControl().value\"\r\n      [attr.indeterminate]=\"indeterminate\"\r\n      [attr.tabindex]=\"tabIndexValue\"\r\n      [attr.disabled]=\"hasDisabledAttribute\"\r\n      [attr.required]=\"hasRequiredAttribute\"\r\n      [attr.name]=\"name\"\r\n      [attr.id]=\"id\"\r\n      [attr.data-testid]=\"testCheckboxId\"\r\n      [attr.aria-checked]=\"ariaCheckedValue\"\r\n      [attr.aria-required]=\"hasRequiredAttribute\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\"\r\n      (change)=\"handleChange($event)\" />\r\n    <span class=\"checkbox-inner-container\">\r\n      <span class=\"checkbox-frame\"></span>\r\n      <span\r\n        class=\"checkbox-background\"\r\n        [style.backgroundColor]=\"backgroundColorValue\"></span>\r\n    </span>\r\n    @if (label) {\r\n      <label\r\n        class=\"checkbox-label\"\r\n        [attr.for]=\"id\">\r\n        {{ label }}\r\n      </label>\r\n    }\r\n  </div>\r\n}\r\n" }]
        }], ctorParameters: () => [{ type: i0.Injector, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Injector)]
                }] }, { type: i0.Renderer2, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Renderer2)]
                }] }, { type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => ElementRef)]
                }] }, { type: i1.CentricFirstLetterUppercasePipe }], propDecorators: { label: [{
                type: Input
            }], indeterminate: [{
                type: Input
            }], isRequired: [{
                type: Input
            }], name: [{
                type: Input
            }], id: [{
                type: Input
            }], testCheckboxId: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], readonly: [{
                type: HostBinding,
                args: ['class.is-readonly']
            }, {
                type: Input
            }], readonlyInputValue: [{
                type: Input
            }], readonlyInputSize: [{
                type: Input
            }] } });

const moduleComponents = [WindmillCheckboxComponent];
class WindmillCheckboxModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillCheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: WindmillCheckboxModule, imports: [CommonModule, FormsModule, ReactiveFormsModule, WindmillCheckboxComponent], exports: [WindmillCheckboxComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillCheckboxModule, providers: [CentricFirstLetterUppercasePipe], imports: [CommonModule, FormsModule, ReactiveFormsModule, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillCheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule, ReactiveFormsModule, ...moduleComponents],
                    exports: [...moduleComponents],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                    providers: [CentricFirstLetterUppercasePipe]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { WINDMILL_CHECKBOX_VALUE_ACCESSOR, WindmillCheckboxComponent, WindmillCheckboxModule };
